// Analysis Slice
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { TermInstance, ContractTerm, RiskAnalysis, AnalysisResult } from '../../types';
import { contractService } from '../../services/contractService';
import { documentService } from '../../services/documentService';

interface AnalysisState {
  currentAnalysis: AnalysisResult | null;
  termInstances: TermInstance[];
  contractTerms: ContractTerm[];
  riskAnalyses: RiskAnalysis[];
  loading: boolean;
  error: string | null;
  filters: {
    riskLevel?: string;
    category?: string;
    searchTerm?: string;
  };
}

const initialState: AnalysisState = {
  currentAnalysis: null,
  termInstances: [],
  contractTerms: [],
  riskAnalyses: [],
  loading: false,
  error: null,
  filters: {},
};

// Async thunks
export const fetchDocumentAnalysis = createAsyncThunk(
  'analysis/fetchDocumentAnalysis',
  async (documentId: string) => {
    return await documentService.getDocumentAnalysis(documentId);
  }
);

export const fetchTermInstances = createAsyncThunk(
  'analysis/fetchTermInstances',
  async (documentId: string) => {
    return await contractService.getTermInstances(documentId);
  }
);

export const fetchContractTerms = createAsyncThunk(
  'analysis/fetchContractTerms',
  async () => {
    return await contractService.getContractTerms();
  }
);

export const fetchRiskAnalyses = createAsyncThunk(
  'analysis/fetchRiskAnalyses',
  async (documentId: string) => {
    return await contractService.getRiskAnalyses(documentId);
  }
);

const analysisSlice = createSlice({
  name: 'analysis',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {};
    },
    clearAnalysis: (state) => {
      state.currentAnalysis = null;
      state.termInstances = [];
      state.riskAnalyses = [];
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch document analysis
      .addCase(fetchDocumentAnalysis.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDocumentAnalysis.fulfilled, (state, action) => {
        state.loading = false;
        state.currentAnalysis = action.payload;
      })
      .addCase(fetchDocumentAnalysis.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch analysis';
      })
      
      // Fetch term instances
      .addCase(fetchTermInstances.fulfilled, (state, action) => {
        state.termInstances = action.payload;
      })
      
      // Fetch contract terms
      .addCase(fetchContractTerms.fulfilled, (state, action) => {
        state.contractTerms = action.payload;
      })
      
      // Fetch risk analyses
      .addCase(fetchRiskAnalyses.fulfilled, (state, action) => {
        state.riskAnalyses = action.payload;
      });
  },
});

export const {
  setFilters,
  clearFilters,
  clearAnalysis,
  clearError,
} = analysisSlice.actions;

export default analysisSlice.reducer;
